import { CoBenefitCategory } from '~/lib/utils/projects'
import { ProjectType } from '~/types/project'

export enum PROJECT_VOLUMES {
  '0_25000' = 'Up to 25,000 tonnes',
  '25000_50000' = '25,000 to 50,000 tonnes',
  '50000_200000' = '50,000 to 200,000 tonnes',
  '200000_PLUS' = '200,000 + tonnes',
  'IM_NOT_SURE' = 'I’m not sure',
}

export type VolumeKeys = keyof typeof PROJECT_VOLUMES

export function isProjectVolumeKey(
  key: string,
): key is keyof typeof PROJECT_VOLUMES {
  return Object.keys(PROJECT_VOLUMES).includes(key)
}

export const CLAIM_STRATEGY = {
  OTHER: 'Other',
  NOT_SURE: 'Not sure',
  NO_CLAIM: 'No claim',
  SBTI_NET_ZERO_TARGET_NEUTRALISATION: 'SBTi Net Zero target neutralisation',
  SBTI_BEYOND_VALUE_CHAIN_MITIGATION: 'SBTi Beyond Value Chain Mitigation',
  VCMI_CARBON_INTEGRITY: 'VCMI Carbon Integrity',
  CARBON_NEUTRAL: 'Carbon neutral',
  NATURE_POSITIVE: 'Nature positive',
  CORSIA_OBLIGATION_PHASE_1: 'CORSIA obligation (Phase 1)',
} as const

export type CLAIM_STRATEGY_VALUES =
  (typeof CLAIM_STRATEGY)[keyof typeof CLAIM_STRATEGY]

export type CLAIM_STRATEGY_KEYS = keyof typeof CLAIM_STRATEGY

export enum RegistryEnum {
  WCC = 'WCC',
  GOLD = 'GOLD',
  CSA = 'CSA',
  Cercarbono = 'Cercarbono',
  PE = 'PE',
  ART = 'ART',
  BioCarbon = 'BioCarbon',
  GCC = 'GCC',
  ACR = 'ACR',
  VCS = 'VCS',
  PV = 'PV',
  CAR = 'CAR',
  COLCX = 'COLCX',
}

export const nonSpecificClaimStrategies = [
  CLAIM_STRATEGY.OTHER,
  CLAIM_STRATEGY.NOT_SURE,
  CLAIM_STRATEGY.NO_CLAIM,
] as CLAIM_STRATEGY_VALUES[]

export type PriceRangesPerProjectType = Record<
  string,
  { q1: number; q3: number }
>

export type LocationSectionType = {
  numberOfMatchingProjectTypes: number
  numberOfProjects: number
  projectTypeOccurrences: Record<string, number>
  numberOfMatchingRegions: number
  numberOfMatchingLocations: number
  relevantVcmData: Array<{ iso2: string; score: number }>
  countryOccurrences: Record<string, number>
}

export type ClaimsExplore = {
  urlParams: string
  projects: ProjectType[]
  countries: Array<{
    iso2: string
    name: string
  }>
  selectedFilters: {
    carbonProfiles: string[]
    projectTypes: string[]
    locations: string[]
    registries: string[]
  }
  registries: Array<{
    name: string
    label: string
  }>
  availableRegistryLabels: string[]
  projectTypeOccurrences: Record<string, number>
  minPrice: number | null
  maxPrice: number | null
  medianPrice: number
  priceRangesPerProjectType: PriceRangesPerProjectType
  numberOfUniqueMatchingProjectTypes: number
  numberOfUniqueMatchingLocations: number
  numberOfUniqueMatchingRegions: number
  relevantVcmData: Array<{ iso2: string; score: number }>
  numberOfProjectsWithCoBenefits: number
  numberOfProjectsWithBenefitsByBenefitType: Record<CoBenefitCategory, number>
  registryOccurrences: Record<string, number>
  uniqueMethodologies: string[]
  countryOccurrences: Record<string, number>
  totalProjectsCount: number
}

export enum PREFERENCES_KEYS {
  CARBON_PROFILES = 'form_carbon_profile',
  CLAIMS_STRATEGY = 'form_claims_strategy',
  VOLUME = 'form_volume_amount',
  PROJECT_LOCATIONS = 'form_project_locations',
  REGISTRIES = 'form_registries',
  PROJECT_TYPES = 'form_project_types',
  OTHER_CLAIM_STRATEGY = 'form_other_claim_strategy',
}

export enum PARAM_KEYS {
  CARBON_PROFILES = 'carbon-profile',
  CLAIMS_STRATEGY = 'claim-strategy',
  VOLUME = 'volume',
  PROJECT_LOCATIONS = 'location',
  REGISTRIES = 'registry',
  PROJECT_TYPES = 'type',
}

export function isParamKey(key: string): key is PARAM_KEYS {
  return Object.values(PARAM_KEYS).includes(key as PARAM_KEYS)
}
